import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import FeedbackImpactReceipt from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { ReportAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { Answer } from './utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { Answer as FeedbackAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { fixImpactDetailAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/SectionFeedback';

const styles = `.modal-header { border-bottom: 1px solid ${COLORS.ctaPrimary} }`;

const feedback = (
  programName?: string,
  organizationName?: string,
): FeedbackDashboardCardProps => ({
  id: 0,
  cnOptedIn: false,
  title: '',
  accountName: '',
  additionalBudgetNotes: '',
  budget: 0,
  budgetNote1: '',
  budgetNote2: '',
  budgetNote1Date: '',
  budgetNote2Date: '',
  claim: 0,
  contactDonor: '',
  contactNpo: [],
  createdAt: '',
  dbId: 0,
  directCashVariance: 0,
  directInkindVariance: 0,
  endDate: '',
  errors: 0,
  feedbackType: 'No Feedback',
  feedbacks: [],
  fiscalYearEnd: '',
  fiscalYearStart: '',
  indirectCashVariance: 0,
  indirectInkindVariance: 0,
  investmentType: '',
  isAllocated: false,
  name: '',
  npoName: '',
  programId: 0,
  programName: '',
  relationshipManagerEmail: '',
  relationshipManagerFirstName: '',
  relationshipManagerId: '',
  relationshipManagerLastName: '',
  sponsorName: '',
  startDate: '',
  tasks: '',
  totalBudgetInitial: 0,
  totalBudgetVariance: 0,
  updatedAt: '',
  version: 'v4',
  warnings: 0,
  reportId: 0,
  organizationName,
  donorName: '',
  contacts: [],
  lastUpdated: undefined,
  dueDate: '',
  status: 'Draft',
  year: undefined,
  portfolioName: programName,
  totalDonations: 0,
  totalCost: 0,
  stage: 'INITIAL',
  isV3: false,
  breadcrumb: undefined,
  reportingType: 'FULL',
  optedInFullReporting: false,
});

export const mapReportBuilderAnswersToFeedbackAnswer = (
  answers: Answer[],
): ReportAnswers => {
  const result: Required<ReportAnswers> = {
    allocation: [],
    beneficiaries: [],
    budget: [],
    dates: [],
    description: [],
    impact_summary: [],
    indicator_details: [],
    location: [],
    narrative: [],
    activities: [],
    finalize_report: [],
    interim_report: [],
    custom_questions: [],
  };
  answers.forEach(x => {
    result[x.question.section].push({
      date1: x.date1,
      date2: x.date2,
      dbId: x.dbId,
      question: x.question,
      questionId: x.questionId,
      questionText: x.question.text,
      questionType: x.question.questionType,
      text: x.text,
    });
  });
  result.indicator_details = fixImpactDetailAnswers(result.indicator_details);
  return result;
};

export const mapFeedbackAnswers = (
  answers: FeedbackAnswers[],
): ReportAnswers => {
  const result: Required<ReportAnswers> = {
    allocation: [],
    beneficiaries: [],
    budget: [],
    dates: [],
    description: [],
    impact_summary: [],
    indicator_details: [],
    location: [],
    narrative: [],
    activities: [],
    finalize_report: [],
    interim_report: [],
    custom_questions: [],
  };
  answers.forEach(x => {
    result[x.question.section].push(x);
  });
  result.indicator_details = fixImpactDetailAnswers(result.indicator_details);
  return result;
};

interface ReportRenderProps {
  reportId: number | string;
  answers: ReportAnswers;
  programName?: string;
}

const StyledDiv = styled.div`
  .download-report-button {
    text-align: center;
    cursor: pointer;
    max-width: 408px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 56px;
    margin-bottom: 56px;
    padding: 28px 20px;
    border-radius: ${SPACERS.extraTiny};
    border: 1px solid ${COLORS.ctaPrimary};
    box-shadow: 0px 0px 22px 0px rgba(0, 0, 0, 0.1);
    svg.download-icon {
      width: 32px;
      height: 32px;
      path {
        fill: ${COLORS.ctaPrimary};
      }
    }
    .download-text {
      margin-top: ${SPACERS.small};
      margin-bottom: ${SPACERS.small};
    }
  }
`;

const ReportRender: React.FC<ReportRenderProps> = ({
  answers,
  reportId,
  programName,
}) => {
  const { user } = useUserActions();
  const [open, setOpen] = useState<boolean>(false);
  const organizationName = user?.attributes?.organizationName || 'Organization';

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <StyledDiv>
      <div className="download-report-button" onClick={handleOpen}>
        <FontAwesomeIcon icon={faDownload} className="download-icon" />
        <TrueImpactText className="download-text">
          View/Download my report draft
        </TrueImpactText>
        <TrueImpactText variant="body-small">
          <strong>Note:</strong> Your report won’t be finalized until after the
          feedback process has been completed.
        </TrueImpactText>
      </div>
      <TrueImpactModal
        open={open}
        hideFooter={true}
        addStyles={styles}
        maxWidth="1350px"
        showPrintButton={true}
        showExpandButton={true}
        onClose={handleClose}
      >
        <FeedbackImpactReceipt
          isThirdParty={false}
          reportId={reportId}
          hideHeader={true}
          answers={answers}
          programName={programName}
          feedback={feedback(programName, organizationName)}
        />
      </TrueImpactModal>
    </StyledDiv>
  );
};

export default ReportRender;
